import { Loader } from '@mantine/core';
import { FC, ReactNode, useMemo } from 'react';

import ExternalLink from '@/components/ExternalLink.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import usePatient from '@/hooks/usePatient.ts';

import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { getInstanceConfig } from '@/utils/configUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { buildPatientFolderUrl } from '@/utils/instanceUtils.ts';

interface Props {
    patientId: string;
    instanceId: string;
}

const TaskPatient: FC<Props> = ({ patientId, instanceId }) => {
    const { patient, is_loading, error } = usePatient({
        user_id: patientId,
        instance_id: instanceId,
        pvs_provider: PVSProvider.DOC_CIRRUS,
    });

    const patient_folder_deep_link: string | undefined = useMemo(() => {
        const config = getInstanceConfig(patient?.instance_id ?? '');
        if (!config?.domain || !config?.pvs_provider || !patient) {
            return '';
        }

        return buildPatientFolderUrl(config.pvs_provider, config.domain, patient.pvs_user_id);
    }, [patient, instanceId]);

    const renderContent = (): ReactNode => {
        if (error || !patient) {
            return <Paragraph className="text-sm text-red-600">Fehler beim Laden der Patientendaten</Paragraph>;
        }

        return (
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                    <Paragraph className="font-bold text-gray-900">
                        {patient.personal.last_name} {patient.personal.first_name}
                    </Paragraph>
                    <ExternalLink
                        text="Patientenakte"
                        href={patient_folder_deep_link}
                        classNames={{ anchor: 'text-blue-600 hover:text-blue-700', text: 'text-sm' }}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <Paragraph className="text-sm text-gray-600">
                        Geburtsdatum: {dayjs(patient.personal.date_of_birth).format('DD.MM.YYYY')}
                    </Paragraph>
                    <Paragraph className="text-sm text-blue-600">
                        {patient.contact.phone_numbers.find((phone) => phone.is_preferred)?.value}
                    </Paragraph>
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
                <Paragraph className="font-semibold text-gray-700">Patient</Paragraph>
                {is_loading && <Loader size="xs" />}
            </div>
            {!is_loading && <div className="border-l-2 border-gray-200 pl-2">{renderContent()}</div>}
        </div>
    );
};

export default TaskPatient;
